import { useState, useEffect } from "react";
import { Button } from "../../Components/UI/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../../Components/UI/dropdown-menu';
import { ChevronDown } from 'lucide-react';
import { Checkbox } from '../../Components/UI/checkbox';
import { Label } from "@radix-ui/react-label";
import { useSearchParams } from "react-router-dom";
import { useSelector } from "react-redux";
import { userIsAdmin } from "../../Store/Reducers";
import { checkAPIResponse } from "../../Utils/APIResponseUtils";
import apiService from "../../Services/APIService";
import { toast } from 'sonner';
import { Eye } from "lucide-react";

const ColumnPreferencesDropdown = ({
  type = "CONN",
  className = "",
  columns = [],
  hideColumns = [],
  columnVisibility = {},
  onUpdate,
  dropdownAlign = "end",
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [tempColumnVisibility, setTempColumnVisibility] = useState(columnVisibility);
  const isAdmin = useSelector(userIsAdmin);

  useEffect(() => {
    setTempColumnVisibility(columnVisibility);
  }, [columnVisibility]);

  useEffect(() => {
    if (isOpen) {
      setTempColumnVisibility(columnVisibility);
    }
  }, [isOpen, columnVisibility]);

  const handleCheckboxChange = (columnKey, checked) => {
    setTempColumnVisibility(prev => ({
      ...prev,
      [columnKey]: checked
    }));
  };

  const handleUpdate = async () => {
    onUpdate(tempColumnVisibility);

    if (isAdmin ) {
      let configData = {
        changeType: "COLPREFS",
        key: "",
        value: '',
        values: '[]'
      }

      if (type == "CONN") {
        configData.key = "CONN";
        let configDataObj = [
          {
            BizName: tempColumnVisibility.bizName,
            ContactName: tempColumnVisibility.contactName,
            Potential: tempColumnVisibility.potential,
            Activity: tempColumnVisibility.activity,
            SinceDT: tempColumnVisibility.sinceDT,
            LastRecDT: tempColumnVisibility.lastRecDT,
            NextTaskDT: tempColumnVisibility.nextTaskDT,
            POCUser: tempColumnVisibility.pocUserID,
            Region: tempColumnVisibility.regionID,
            Source: tempColumnVisibility.source,
            City: tempColumnVisibility.city,
            State: tempColumnVisibility.state,
            TaxID: tempColumnVisibility.taxID,
            Country: tempColumnVisibility.country,
            Mobile: tempColumnVisibility.mobile,
            Email: tempColumnVisibility.email,
            Feedback: tempColumnVisibility.feedback,
            Segment: tempColumnVisibility.segment,
            Tags: tempColumnVisibility.tags
          }
        ];
        configData.values = JSON.stringify(configDataObj);
      }
      else if (type == "THR") {
        configData.key = "THR";
        let configDataObj = [
          {
            Type: tempColumnVisibility.type,
            BizName: tempColumnVisibility.bizName,
            ContactName: tempColumnVisibility.contactName,
            Product: tempColumnVisibility.mainProduct,
            Amount: tempColumnVisibility.bizProspect,
            Received: tempColumnVisibility.sinceDT,
            Due: tempColumnVisibility.dueDT,
            Ref: tempColumnVisibility.reference,
            Status: tempColumnVisibility.status,
            NextTask: tempColumnVisibility.nextTaskDT,
            Executive: tempColumnVisibility.pocUserName,
            Source: tempColumnVisibility.source,
            Mobile: tempColumnVisibility.mobile,
            Email: tempColumnVisibility.email,
            City: tempColumnVisibility.city,
            LastInteraction: tempColumnVisibility.lastInteraction
          }
        ];
        configData.values = JSON.stringify(configDataObj);
      }

      try {
        const extraParams = { isWeb: true };
        const result = await apiService.sendChange(3, 'set', configData, extraParams);
        checkAPIResponse(result);
        setIsOpen(false);
        return result;

      } catch (error) {
        toast.error(error.message || `Failed to store preferences.`);
        setTempColumnVisibility(columnVisibility);
        throw error;
      }

    }
    else {
      if (type == "CONN") {
        localStorage.setItem("ConnColPrefs", JSON.stringify(tempColumnVisibility));

        window.dispatchEvent(new StorageEvent('storage', {
          key: 'ConnColPrefs',
          newValue: JSON.stringify(tempColumnVisibility),
          url: window.location.href
        }));
      }
      else if (type == "THR") {
        localStorage.setItem("ThrColPrefs", JSON.stringify(tempColumnVisibility));

        window.dispatchEvent(new StorageEvent('storage', {
          key: 'ThrColPrefs',
          newValue: JSON.stringify(tempColumnVisibility),
          url: window.location.href
        }));
      }
      else if (type == "MIS") {
        localStorage.setItem("UsageMISColPrefs", JSON.stringify(tempColumnVisibility));

        window.dispatchEvent(new StorageEvent('storage', {
          key: 'UsageMISColPrefs',
          newValue: JSON.stringify(tempColumnVisibility),
          url: window.location.href
        }));
      }
      setIsOpen(false);
    }
  };

  const handleCancel = () => {
    setTempColumnVisibility(columnVisibility);
    setIsOpen(false);
  };

  // Filter columns and split into two arrays
  const filteredColumns = columns.filter(
    (column) => !hideColumns.includes(column.key)
  );
  const midpoint = Math.ceil(filteredColumns.length / 2);
  const firstColumnItems = filteredColumns.slice(0, midpoint);
  const secondColumnItems = filteredColumns.slice(midpoint);

  return (
    <DropdownMenu open={isOpen} onOpenChange={setIsOpen}>
      <DropdownMenuTrigger asChild>
        <Button
          className={`button-config text-base sahi-primary-button flex items-center py-[6px] px-[10px] ${className}`}
        >
          <Eye className="h-5 w-5" />
          Columns
          <ChevronDown className="h-4 w-4" />
        </Button>
      </DropdownMenuTrigger>

      <DropdownMenuContent
        className="w-[480px] bg-white border border-gray-200 shadow-lg rounded-md p-3"
        align={dropdownAlign}
      >
        <div className="grid grid-cols-2 gap-4 max-h-100 overflow-y-auto">
          {/* First Column */}
          <div className="space-y-2">
            {firstColumnItems.map((column) => (
              <div
                key={column.key}
                className="flex items-center gap-2 px-2 py-2 hover:bg-gray-50 rounded-md cursor-pointer"
              >
                <Checkbox
                  id={`column-${column.key}`}
                  checked={tempColumnVisibility[column.key] || false}
                  onCheckedChange={(checked) => handleCheckboxChange(column.key, checked)}
                  disabled={column.key === 'bizName' || (type === 'MIS' && column.key === 'agencyName')}
                />
                <Label
                  htmlFor={`column-${column.key}`}
                  className="text-sm font-medium text-gray-900 cursor-pointer flex-1"
                >
                  {column.label}
                </Label>
              </div>
            ))}
          </div>

          {/* Second Column */}
          <div className="space-y-2">
            {secondColumnItems.map((column) => (
              <div
                key={column.key}
                className="flex items-center gap-2 px-2 py-2 hover:bg-gray-50 rounded-md cursor-pointer"
              >
                <Checkbox
                  id={`column-${column.key}`}
                  checked={tempColumnVisibility[column.key] || false}
                  onCheckedChange={(checked) => handleCheckboxChange(column.key, checked)}
                  disabled={column.key === 'bizName' || (type === 'MIS' && column.key === 'agencyName')}
                />
                <Label
                  htmlFor={`column-${column.key}`}
                  className="text-sm font-medium text-gray-900 cursor-pointer flex-1"
                >
                  {column.label}
                </Label>
              </div>
            ))}
          </div>
        </div>

        <div className="flex gap-2 mt-4">
          <Button
            className="flex-1 sahi-primary-button"
            onClick={handleUpdate}
          >
            Update
          </Button>
          <Button
            variant="outline"
            className="flex-1 sahi-secondary-button"
            onClick={handleCancel}
          >
            Cancel
          </Button>
        </div>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

export default ColumnPreferencesDropdown;